import { useRef, useState, useEffect, useMemo } from "react";
import type { FC } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import useWhiteLabelConfig from "src/contexts/config-context";
import useHousehold from "src/contexts/household-context";

import { Scrollbar } from "src/components/scrollbar";
import { usePathname } from "src/hooks/use-pathname";
import type { NavColor } from "src/types/settings";

import type { Section } from "../config";
import { SideNavSection } from "./side-nav-section";

const SIDE_NAV_WIDTH: number = 280;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();

  return useMemo((): Record<string, string> => {
    switch (color) {
      case "blend-in":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": theme.palette.background.default,
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.neutral[100],
            "--nav-logo-border": theme.palette.neutral[100],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[900],
          };
        }

      case "discreet":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[900],
            "--nav-color": theme.palette.neutral[100],
            "--nav-border-color": theme.palette.neutral[700],
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[600],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[700],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": "#f5f5f5",
            "--nav-color": theme.palette.text.primary,
            "--nav-border-color": theme.palette.divider,
            "--nav-logo-border": theme.palette.neutral[200],
            "--nav-section-title-color": theme.palette.neutral[500],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": theme.palette.action.hover,
            "--nav-item-active-bg": theme.palette.action.selected,
            "--nav-item-active-color": theme.palette.text.primary,
            "--nav-item-disabled-color": theme.palette.neutral[400],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[400],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[900],
          };
        }

      case "evident":
        if (theme.palette.mode === "dark") {
          return {
            "--nav-bg": theme.palette.neutral[800],
            "--nav-color": theme.palette.common.white,
            "--nav-border-color": "transparent",
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.common.white,
            "--nav-item-disabled-color": theme.palette.neutral[500],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[500],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        } else {
          return {
            "--nav-bg": theme.palette.neutral[800],
            "--nav-color": theme.palette.common.white,
            "--nav-border-color": "transparent",
            "--nav-logo-border": theme.palette.neutral[700],
            "--nav-section-title-color": theme.palette.neutral[400],
            "--nav-item-color": "#1a1a1a",
            "--nav-item-hover-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-bg": "rgba(255, 255, 255, 0.04)",
            "--nav-item-active-color": theme.palette.common.white,
            "--nav-item-disabled-color": theme.palette.neutral[500],
            "--nav-item-icon-color": "#595959",
            "--nav-item-icon-active-color": theme.palette.primary.main,
            "--nav-item-icon-disabled-color": theme.palette.neutral[500],
            "--nav-item-chevron-color": "#595959",
            "--nav-scrollbar-color": theme.palette.neutral[400],
          };
        }

      default:
        return {};
    }
  }, [theme, color]);
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
  onAddPlanClick: () => void;
  admin: boolean;
}

// Helper function to find which specific item contains a path
const findActiveItem = (sections: Section[], pathname: string | null): { sectionIndex: number; itemIndex: number } | null => {
  if (!pathname) return null;
  
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    for (let itemIndex = 0; itemIndex < section.items.length; itemIndex++) {
      const item = section.items[itemIndex];
      if (item.items) {
        // Check if any child item matches the current path
        for (const childItem of item.items) {
          if (childItem.path && (
            pathname === childItem.path || 
            pathname.startsWith(childItem.path + '/') || 
            pathname.startsWith(childItem.path + '?')
          )) {
            return { sectionIndex, itemIndex };
          }
        }
      } else if (item.path && (
        pathname === item.path || 
        pathname.startsWith(item.path + '/') || 
        pathname.startsWith(item.path + '?')
      )) {
        return { sectionIndex, itemIndex };
      }
    }
  }
  return null;
};

// Helper function to find which sections should be open based on selectedUdId
const findOpenSectionIndices = (sections: Section[], selectedUdId: string | null): number[] => {
  if (!selectedUdId) return [];
  
  const openSections = new Set<number>();
  
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    for (const item of section.items) {
      if (item.items) {
        // Check if any child item contains the selected user ID
        const hasMatchingChild = item.items.some(childItem => {
          if (childItem.path) {
            const match = childItem.path.match(/^\/([^/]+)(?:\/|$)/);
            return match && match[1] === selectedUdId;
          }
          return false;
        });
        
        if (hasMatchingChild) {
          openSections.add(sectionIndex);
          break; // Found a match in this section, no need to check other items
        }
      }
    }
  }
  return Array.from(openSections);
};

export const SideNav: FC<SideNavProps> = (props) => {
  const {
    color = "evident",
    sections = [],
    admin
  } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);
  const { selectedUdId, setSelectedUdId } = useHousehold();

  const navigate = useNavigate();
  const { professional_group } = useWhiteLabelConfig();

  // Navigation state managed in local state
  const [activeItem, setActiveItem] = useState<{ sectionIndex: number; itemIndex: number } | null>(null);
  const [openSectionIndices, setOpenSectionIndices] = useState<number[]>([]);
  const [currentSelectedUdId, setCurrentSelectedUdId] = useState<string | null>(selectedUdId);

  // Update navigation state when pathname changes
  useEffect(() => {
    const newActiveItem = findActiveItem(sections, pathname);
    setActiveItem(newActiveItem);
    
    // Extract the selectedUdId from the current pathname
    if (pathname && newActiveItem) {
      const section = sections[newActiveItem.sectionIndex];
      const item = section?.items[newActiveItem.itemIndex];
      if (item?.items) {
        // Find which child item matches the current path
        for (const childItem of item.items) {
          if (childItem.path && (
            pathname === childItem.path || 
            pathname.startsWith(childItem.path + '/') || 
            pathname.startsWith(childItem.path + '?')
          )) {
            const match = childItem.path.match(/^\/([^/]+)(?:\/|$)/);
            if (match) {
              const pathUdId = match[1];
              setCurrentSelectedUdId(pathUdId);
              setSelectedUdId(pathUdId); // Update the global state too
              
              // Immediately update open sections to include the active section
              const newOpenSectionIndices = findOpenSectionIndices(sections, pathUdId);
              setOpenSectionIndices(newOpenSectionIndices);
              break;
            }
          }
        }
      }
    }
  }, [pathname, sections, setSelectedUdId]);

  // Also update open sections when selectedUdId changes independently (for manual selection)
  useEffect(() => {
    if (currentSelectedUdId) {
      const newOpenSectionIndices = findOpenSectionIndices(sections, currentSelectedUdId);
      setOpenSectionIndices(newOpenSectionIndices);
    }
  }, [sections, currentSelectedUdId]);

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current && professional_group?.app_logo && professional_group?.total_app_logo_multiplier) {
        const { naturalWidth, naturalHeight } = imageRef.current;
        const aspectRatio = naturalWidth / naturalHeight;
        let maxWidth = 170;
        let maxHeight = 30;

        // Adjust maxWidth for aspect ratios
        if (aspectRatio < 2.9) {
          maxHeight = 48;
        }
        if (aspectRatio >= 2.9 && aspectRatio <= 4) {
          maxHeight = 40;
        }
        if (aspectRatio > 4) {
          maxHeight = 35;
        }

        if (naturalWidth > maxWidth || naturalHeight > maxHeight) {
          if (maxWidth / aspectRatio < maxHeight) {
            setDimensions({ width: maxWidth * professional_group?.total_app_logo_multiplier, height: maxWidth / aspectRatio * professional_group?.total_app_logo_multiplier });
          } else {
            setDimensions({
              width: maxHeight * aspectRatio * professional_group?.total_app_logo_multiplier,
              height: maxHeight * professional_group?.total_app_logo_multiplier,
            });
          }
        } else {
          setDimensions({ width: naturalWidth * professional_group?.total_app_logo_multiplier, height: naturalHeight * professional_group?.total_app_logo_multiplier });
        }
      }
    };

    const img = imageRef.current;

    if (img && professional_group?.app_logo) {
      img.onload = handleResize;
      img.src = professional_group?.app_logo;
    }

    return () => {
      if (img) {
        img.onload = null;
      }
    };
  }, [professional_group?.app_logo, professional_group?.total_app_logo_multiplier]);

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: "var(--nav-bg)",
          borderRightColor: "#e7e7e7",
          borderRightStyle: "solid",
          borderRightWidth: 1,
          color: "var(--nav-color)",
          width: SIDE_NAV_WIDTH,
          paddingTop:
            admin ? "48px" : "0px",
        },
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: "100%",
          "& .simplebar-content": {
            height: "100%",
          },
          "& .simplebar-scrollbar:before": {
            background: "var(--nav-scrollbar-color)",
          },
        }}
      >
        <Stack sx={{ height: "100%" }}>
          <Stack
            alignItems="center"
            direction="row"
            spacing={2}
            sx={{ px: 3, pt: 3, pb: 5 }}
          >
            <Box
              onClick={() => navigate("/")}
            >
              <img
                ref={imageRef}
                src={professional_group?.app_logo || undefined}
                style={{ width: dimensions.width, height: dimensions.height }}
              />
            </Box>
          </Stack>
          
          <Stack
            component="nav"
            spacing={1}
            sx={{
              flexGrow: 1,
              px: 2.5,
            }}
          >
            {sections.map((section, sectionIndex) => {
              const isActiveSection = activeItem?.sectionIndex === sectionIndex;
              const shouldBeOpen = openSectionIndices.includes(sectionIndex) || isActiveSection;
            
              
              return (
                <SideNavSection
                  items={section.items}
                  key={sectionIndex}
                  pathname={pathname}
                  subheader={section.subheader}
                  activeItemIndex={isActiveSection ? activeItem.itemIndex : -1}
                  isOpen={shouldBeOpen}
                  selectedUdId={currentSelectedUdId}
                />
              );
            })}
          </Stack>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf<NavColor>(["blend-in", "discreet", "evident"]),
  sections: PropTypes.array,
};
